// WebSocketProvider.js
import React, { createContext, useContext,useState, useEffect, useRef, useCallback } from 'react';
import io from 'socket.io-client';
import {NOTIFICATION_SERVER_URL} from "./../services/api"

const WebSocketContext = createContext(null);

export const useWebSocket = () => useContext(WebSocketContext);

export const WebSocketProvider = ({ children, userGoogleId }) => {
  
  const socketRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  // Map to track callbacks: eventName -> [callback, ...]
  const listenersRef = useRef(new Map());

  // Initialize Socket.IO connection
  useEffect(() => {
    const socket = io(NOTIFICATION_SERVER_URL, {
      withCredentials: true, // 👈 Ensures cookies are sent along with the request
      auth: { token: userGoogleId },
      path: '/sockets', // adjust as needed
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('Connected to the notification server via Socket.IO');
      setIsConnected(true);
    });

    socket.on('disconnect', () => {
      console.log('Disconnected from the notification server via Socket.IO');
      setIsConnected(false);
    });

    // Cleanup on unmount
    return () => {
      socket.disconnect();
    };
  }, [NOTIFICATION_SERVER_URL, userGoogleId]);

  /**
   * subscribe(subscriptions)
   *
   * subscriptions: an object where each key is an event name and the value is a callback.
   * Example:
   *   subscribe({
   *     ALERT: (data) => { console.log('Alert:', data); },
   *     UPDATE: (data) => { console.log('Update:', data); },
   *   });
   *
   * Returns an unsubscribe function to remove these callbacks.
   */
  const subscribe = useCallback((subscriptions) => {
    // if (!socketRef.current) {
    //   console.warn("Socket is not connected yet. Subscription skipped.");
    //   return () => {}; // Return a no-op unsubscribe function.
    // }

    Object.keys(subscriptions).forEach((eventName) => {
      const callback = subscriptions[eventName];
      if (!listenersRef.current.has(eventName)) {
        listenersRef.current.set(eventName, []);
      }
      listenersRef.current.get(eventName).push(callback);
      socketRef.current.on(eventName, callback);
    });

    return () => {
      Object.keys(subscriptions).forEach((eventName) => {
        const callback = subscriptions[eventName];
        socketRef.current.off(eventName, callback);
        if (listenersRef.current.has(eventName)) {
          const callbacks = listenersRef.current.get(eventName);
          const newCallbacks = callbacks.filter((cb) => cb !== callback);
          if (newCallbacks.length > 0) {
            listenersRef.current.set(eventName, newCallbacks);
          } else {
            listenersRef.current.delete(eventName);
          }
        }
      });
    };
  }, []); // Empty dependency array so subscribe remains stable

  /**
   * sendMessage(eventName, message)
   *
   * Emits a message on the given event name.
   */
  const sendMessage = (eventName, message) => {
    if (socketRef.current && socketRef.current.connected) {
      socketRef.current.emit(eventName, message);
    }
  };

  return (
    <WebSocketContext.Provider value={{ subscribe, isConnected,sendMessage, socket: socketRef.current }}>
      {children}
    </WebSocketContext.Provider>
  );
};
